import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import {
  Wallet, TrendingUp, Clock, Gift, Loader2, Send, Wifi, WifiOff,
  Download, FileText
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { DualPrice } from './DualPrice';
import { useWebSocket } from '../hooks/useWebSocket';
import { PayoutMethodsCard } from './PayoutMethodsCard';

export function WalletPage() {
  const { api, user: authUser } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [unitHistory, setUnitHistory] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferData, setTransferData] = useState({ to_username: '', amount: '' });
  const [transferring, setTransferring] = useState(false);
  const [eligibility, setEligibility] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [walletRes, historyRes, eligRes] = await Promise.all([
        api.get('/api/wallet/balance'),
        api.get('/api/wallet/history'),
        api.get('/api/wallet/eligibility').catch(() => ({ data: null }))
      ]);
      setWallet(walletRes.data);
      setHistory(historyRes.data.history || []);
      if (eligRes.data) setEligibility(eligRes.data);
      // Fetch unit value history
      try {
        const { data: uvData } = await api.get('/api/wallet/unit-value-history?days=90');
        setUnitHistory(uvData.history || []);
      } catch (e) {
        console.debug('Unit-value history unavailable:', e?.message || e);
      }
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleWsMessage = useCallback((msg) => {
    if (msg.type === 'wallet_update' || msg.type === 'commission') {
      toast.info('Wallet updated in real-time');
      fetchData();
    }
  }, [fetchData]);

  const { connected } = useWebSocket(authUser?.id, handleWsMessage);

  const downloadCertificate = async () => {
    try {
      const res = await api.get('/api/wallet/share-certificate/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'AGA_Share_Certificate.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Share certificate downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download certificate');
    }
  };

  const shareCertificate = async () => {
    try {
      const res = await api.get('/api/wallet/share-certificate/pdf', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const file = new File([blob], 'AGA_Share_Certificate.pdf', { type: 'application/pdf' });

      const shareText = `My All Global Access Membership Share Certificate — ${Number(wallet?.shares_count || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} shares issued. Join AGA: ${window.location.origin}`;

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'AGA Share Certificate',
          text: shareText,
          files: [file],
        });
        toast.success('Certificate shared');
      } else if (navigator.share) {
        await navigator.share({
          title: 'AGA Share Certificate',
          text: shareText,
          url: window.location.origin,
        });
        toast.success('Shared');
      } else {
        await navigator.clipboard.writeText(shareText);
        toast.success('Share link copied to clipboard');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        toast.error('Sharing failed');
      }
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferData.to_username || !transferData.amount || parseFloat(transferData.amount) <= 0) return;
    setTransferring(true);
    try {
      const { data } = await api.post('/api/wallet/transfer', {
        to_username: transferData.to_username,
        amount: parseFloat(transferData.amount)
      });
      toast.success(data.message);
      setTransferOpen(false);
      setTransferData({ to_username: '', amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const chartData = history.slice().reverse().map(h => ({
    date: new Date(h.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric' }),
    value: h.balance_after
  }));

  if (loading) {
    return (
      <MemberSidebarLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </MemberSidebarLayout>
    );
  }

  const hasShares = (wallet?.shares_count || 0) > 0;

  return (
    <MemberSidebarLayout>
      <div className="p-6 lg:p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">My Wallet</h1>
              <p className="text-[#A0B4CC] mt-1">Track your membership unit value and earnings</p>
            </div>
            <div className="flex items-center gap-2 text-xs" data-testid="ws-status">
              {connected ? (
                <><Wifi className="w-3.5 h-3.5 text-[#00E676]" /><span className="text-[#00E676]">Live</span></>
              ) : (
                <><WifiOff className="w-3.5 h-3.5 text-[#5C7599]" /><span className="text-[#5C7599]">Offline</span></>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Membership Unit Value Card */}
            <Card className="bg-gradient-to-br from-[#041A3B] to-[#0A254D] border-[#00E5FF]/30 md:col-span-2">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                  <div>
                    <p className="text-[#A0B4CC] mb-2">Membership Unit Value</p>
                    <p className="font-outfit text-4xl font-bold text-white mb-2" data-testid="membership-unit-value">
                      <DualPrice php={wallet?.initial_value || 0} phpDecimals={2} usdClassName="text-lg text-[#A0B4CC] font-medium ml-2" />
                    </p>
                    {hasShares && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-[#A0B4CC] text-sm">
                          {Number(wallet?.shares_count || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} shares
                        </span>
                        <span className="text-[#5C7599] text-xs">|</span>
                        <span className="text-[#A0B4CC] text-sm">
                          Non-withdrawable
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {hasShares && wallet?.php_usd_rate && (
                      <div className="text-right">
                        <p className="text-[#A0B4CC] text-sm">USD Equivalent</p>
                        <p className="text-white font-mono text-xs">
                          ${(wallet?.membership_unit_value_usd || 0).toFixed(4)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Referral Earnings Card */}
            <Card className="bg-[#041A3B] border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#FFEA00]/10 flex items-center justify-center">
                    <Gift className="w-6 h-6 text-[#FFEA00]" />
                  </div>
                  <div>
                    <p className="text-[#A0B4CC] text-sm">Referral Earnings</p>
                    <p className="font-outfit text-2xl font-semibold text-white">
                      <DualPrice php={wallet?.referral_earnings || 0} phpDecimals={2} usdClassName="text-sm text-[#A0B4CC] font-normal ml-1" />
                    </p>
                  </div>
                </div>
                <p className="text-[#5C7599] text-xs">
                  Available for withdrawal (min. PHP 100)
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Unit Value History Chart & Certificate */}
          {hasShares && (
            <div className="grid lg:grid-cols-3 gap-6 mb-6">
              {/* Chart */}
              <Card className="bg-[#041A3B] border-white/10 lg:col-span-2">
                <CardContent className="p-6">
                  <h3 className="font-outfit text-base font-semibold text-white mb-4">Unit Value History</h3>
                  {unitHistory.length > 1 ? (
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={unitHistory}>
                          <defs>
                            <linearGradient id="uvGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#00E5FF" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#00E5FF" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                          <XAxis dataKey="date" stroke="#5C7599" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                          <YAxis stroke="#5C7599" tick={{ fontSize: 10 }} tickFormatter={v => `$${v}`} domain={['dataMin', 'dataMax']} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#041A3B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
                            formatter={(v) => [`$${Number(v).toFixed(7)}`, 'Unit Value']}
                            labelFormatter={(l) => `Date: ${l}`}
                          />
                          <Area type="monotone" dataKey="unit_value_usd" stroke="#00E5FF" fill="url(#uvGrad)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-[#5C7599] text-sm">
                      <div className="text-center">
                        <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p>Chart data building up — check back after a few days</p>
                        <p className="text-[10px] mt-1">Current unit value: ${(wallet?.membership_unit_value_usd && wallet.php_usd_rate ? (wallet.membership_unit_value_usd / wallet.shares_count).toFixed(7) : '0.0000100')}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Share Certificate Card */}
              <Card className="bg-gradient-to-br from-[#041A3B] to-[#0A254D] border-[#00E676]/20">
                <CardContent className="p-6 flex flex-col justify-between h-full">
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-[#00E676]/10 flex items-center justify-center mb-4">
                      <FileText className="w-6 h-6 text-[#00E676]" />
                    </div>
                    <h3 className="font-outfit text-base font-semibold text-white mb-1">Share Certificate</h3>
                    <p className="text-[#5C7599] text-xs mb-2">
                      Download your official AGA membership share certificate with your allocated shares and unit value.
                    </p>
                    <div className="space-y-1 text-xs text-[#A0B4CC] mb-4">
                      <p>{Number(wallet?.shares_count || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} shares allocated</p>
                      <p>Par: $0.0000100 / share</p>
                    </div>
                  </div>
                  <Button
                    onClick={downloadCertificate}
                    className="w-full bg-[#00E676]/10 text-[#00E676] hover:bg-[#00E676]/20 border border-[#00E676]/30"
                    data-testid="download-certificate-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                  <Button
                    onClick={shareCertificate}
                    variant="ghost"
                    className="w-full text-[#00E5FF] hover:bg-[#00E5FF]/10 mt-2"
                    data-testid="share-certificate-btn"
                  >
                    <Send className="w-4 h-4 mr-2" /> Share Certificate
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Eligibility Warning */}
          {eligibility && !eligibility.eligible && (
            <div className="mb-6 p-4 rounded-lg bg-[#FF1744]/10 border border-[#FF1744]/30">
              <p className="text-[#FF1744] text-sm font-medium">
                Your wallet balance is below the minimum threshold (PHP {eligibility.lowest_plan_price?.toLocaleString()}). 
                Member benefits are suspended. Please resubscribe to continue.
              </p>
            </div>
          )}
          
          {eligibility?.is_lifetime && (
            <div className="mb-6 p-4 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/30">
              <p className="text-[#00E5FF] text-sm font-medium">
                You have a lifetime subscription. Your membership never expires.
              </p>
            </div>
          )}

          {/* Transfer Balance */}
          <div className="flex flex-wrap gap-3 mb-8">
            <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="transfer-btn">
                  <Send className="w-4 h-4 mr-2" />
                  Transfer Balance
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#041A3B] border-white/10 max-w-sm">
                <DialogHeader>
                  <DialogTitle className="text-white">Transfer Wallet Balance</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleTransfer} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white">Recipient (name or email)</Label>
                    <Input 
                      value={transferData.to_username}
                      onChange={(e) => setTransferData({ ...transferData, to_username: e.target.value })}
                      className="bg-[#000B18] border-white/20 text-white"
                      placeholder="Enter recipient's name or email"
                      required
                      data-testid="transfer-recipient"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Amount (PHP)</Label>
                    <Input 
                      type="number"
                      value={transferData.amount}
                      onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                      className="bg-[#000B18] border-white/20 text-white"
                      placeholder="0.00"
                      min="1"
                      step="0.01"
                      required
                      data-testid="transfer-amount"
                    />
                    <p className="text-[#5C7599] text-xs">
                      Available: {formatCurrency(wallet?.total_value || wallet?.current_value || 0)}
                    </p>
                  </div>
                  <Button type="submit" disabled={transferring} className="w-full btn-primary" data-testid="confirm-transfer-btn">
                    {transferring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Confirm Transfer
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Linked Payout Methods (for PayMongo automated disbursements) */}
          <div className="mb-8">
            <PayoutMethodsCard />
          </div>

          <Card className="bg-[#041A3B] border-white/10 mb-8">
            <CardHeader>
              <CardTitle className="font-outfit text-white">Value History</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00E5FF" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#00E5FF" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="date" stroke="#5C7599" />
                      <YAxis stroke="#5C7599" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#041A3B', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Area type="monotone" dataKey="value" stroke="#00E5FF" fillOpacity={1} fill="url(#colorValue)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-[#5C7599]">
                  No history data yet
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#041A3B] border-white/10">
            <CardHeader>
              <CardTitle className="font-outfit text-white">Transaction History</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="py-12 text-center">
                  <Clock className="w-12 h-12 text-[#5C7599] mx-auto mb-4" />
                  <p className="text-[#A0B4CC]">No transactions yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((tx, i) => (
                    <div key={tx.id || `tx-${i}`} className="flex items-center justify-between p-4 rounded-lg bg-[#000B18]">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          tx.type === 'subscription' ? 'bg-[#007AFF]/10' : 'bg-[#00E676]/10'
                        }`}>
                          {tx.type === 'subscription' 
                            ? <Wallet className="w-5 h-5 text-[#007AFF]" />
                            : <Gift className="w-5 h-5 text-[#00E676]" />
                          }
                        </div>
                        <div>
                          <p className="text-white font-medium">{tx.description}</p>
                          <p className="text-[#5C7599] text-sm">
                            {new Date(tx.created_at).toLocaleDateString('en-PH', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-mono ${tx.amount >= 0 ? 'text-[#00E676]' : 'text-[#FF1744]'}`}>
                          {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                        </p>
                        <p className="text-[#5C7599] text-xs">Balance: {formatCurrency(tx.balance_after)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="mt-8 p-4 rounded-lg bg-[#041A3B]/50 border border-white/5">
            <p className="text-[#5C7599] text-xs text-center">
              <strong>Disclaimer:</strong> Wallet values reflect participation tracking only. 
              Value may increase or decrease based on portfolio performance. 
              Not a financial product. No guaranteed returns.
            </p>
          </div>
      </div>
    </MemberSidebarLayout>
  );
}
